import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const usePitchDrawingZones = ({
  displayedEvents,
  convertCoordinatesWithPadding,
  getAllZones,
}) => {
  const [selectedZones, setSelectedZones] = useState([]);
  const [zoneRectangles, setZoneRectangles] = useState({});

  const [drawing, setDrawing] = useState(false);
  const [shapeType] = useState("rectangle");
  const [drawnShapes, setDrawnShapes] = useState([]);
  const [previewShape, setPreviewShape] = useState(null);

  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const [currentX, setCurrentX] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [drawingStarted, setDrawingStarted] = useState(false);

  const pitchRef = useRef(null);
  const startDrag = useRef(false);

  const [drawMarkers, setDrawMarkers] = useState([]);
  const [zoneMarkers, setZoneMarkers] = useState([]);

  const getMousePositionSVG = useCallback((event, pos) => {
    try {
      if (pitchRef?.current) {
        let point = pitchRef.current.createSVGPoint();

        if (point) {
          point.x = event.clientX;
          point.y = event.clientY;
          point = point.matrixTransform(pitchRef.current.getScreenCTM().inverse());

          return point;
        }

        return pos;
      }

      return pos;
    } catch {
      return pos;
    }
  }, []);

  const recalculateCountEvents = useCallback(
    (newShape) => {
      const count = displayedEvents?.filter((event) => {
        const pos = convertCoordinatesWithPadding(event.coord_x, event.coord_y);

        return (
          pos.x >= newShape.x &&
          pos.x <= newShape.x + newShape.width &&
          pos.y >= newShape.y &&
          pos.y <= newShape.y + newShape.height
        );
      }).length;

      return {
        ...newShape,
        eventCount: count,
      };
    },
    [displayedEvents, convertCoordinatesWithPadding]
  );

  const handleMouseDown = useCallback(
    (event) => {
      const isCustomShape = event?.target?.classList?.contains("custom-shape");

      if (isCustomShape) {
        startDrag.current = true;
        event.target.classList.add("draggble");

        const currX = event.target.getAttribute("x");
        const currY = event.target.getAttribute("y");
        startXRef.current = +currX - event.clientX;
        startYRef.current = +currY - event.clientY;
        return;
      }

      if (!drawing) {
        return;
      }

      const { x, y } = getMousePositionSVG(event, {
        x: event.clientX,
        y: event.clientY,
      });

      setStartX(x);
      setStartY(y);
      startXRef.current = x;
      startYRef.current = y;
      setCurrentX(x);
      setCurrentY(y);
      setDrawingStarted(true);
    },
    [drawing, getMousePositionSVG]
  );

  const handleMouseMove = useCallback(
    (event) => {
      if (startDrag.current) {
        const draggableShape = document.querySelector(".draggble");

        if (draggableShape) {
          draggableShape.setAttribute("x", event.clientX + startXRef.current);
          draggableShape.setAttribute("y", event.clientY + startYRef.current);
        }

        return;
      }

      if (!(drawing && drawingStarted)) {
        return;
      }

      const { x, y } = getMousePositionSVG(event, {
        x: event.clientX,
        y: event.clientY,
      });

      setCurrentX(x);
      setCurrentY(y);

      const width = Math.abs(x - startX);
      const height = Math.abs(y - startY);
      const minX = Math.min(x, startX);
      const minY = Math.min(y, startY);

      setPreviewShape({
        type: shapeType,
        x: minX,
        y: minY,
        width: shapeType === "rectangle" ? width : 0,
        height: shapeType === "rectangle" ? height : 0,
      });
    },
    [drawing, drawingStarted, getMousePositionSVG, shapeType, startX, startY]
  );

  const handleMouseUp = useCallback(() => {
    if (startDrag.current) {
      const draggableShape = document.querySelector(".draggble");

      if (draggableShape) {
        const x = +draggableShape.getAttribute("x");
        const y = +draggableShape.getAttribute("y");
        const id = draggableShape.getAttribute("id");

        if (id) {
          setDrawnShapes((prev) => {
            const nextShapes = [...prev];
            const foundedShapeIdx = nextShapes.findIndex((item) => item.id === +id);

            if (foundedShapeIdx < 0) {
              return prev;
            }

            const nextShape = recalculateCountEvents(nextShapes[foundedShapeIdx]);
            nextShapes[foundedShapeIdx] = {
              ...nextShape,
              x,
              y,
            };

            return nextShapes;
          });

          document
            .querySelectorAll(".draggble")
            ?.forEach((node) => node?.classList?.remove("draggble"));
        }
      }

      startDrag.current = false;
      return;
    }

    if (!(drawing && drawingStarted)) {
      return;
    }

    const width = Math.abs(currentX - startX);
    const height = Math.abs(currentY - startY);
    const minX = Math.min(startX, currentX);
    const minY = Math.min(startY, currentY);

    const newShape = {
      type: shapeType,
      x: minX,
      y: minY,
      width: shapeType === "rectangle" ? width : 0,
      height: shapeType === "rectangle" ? height : 0,
      id: Date.now(),
    };

    setDrawnShapes((prev) => [...prev, recalculateCountEvents(newShape)]);
    setDrawingStarted(false);
    setPreviewShape(null);
  }, [
    currentX,
    currentY,
    drawing,
    drawingStarted,
    recalculateCountEvents,
    shapeType,
    startX,
    startY,
  ]);

  const isMarkerInRectangle = useCallback(
    (marker, rectangle) => {
      const { x, y, width, height } = rectangle;
      const pos = convertCoordinatesWithPadding(marker.coord_x, marker.coord_y);

      return pos.x >= x && pos.x <= x + width && pos.y >= y && pos.y <= y + height;
    },
    [convertCoordinatesWithPadding]
  );

  useEffect(() => {
    const markersInRectangles = displayedEvents
      ?.filter((event) =>
        drawnShapes.some(
          (shape) => shape.type === "rectangle" && isMarkerInRectangle(event, shape)
        )
      )
      .map((event) => event.id);

    setDrawMarkers(markersInRectangles || []);
  }, [displayedEvents, drawnShapes, isMarkerInRectangle]);

  const handleZoneSelection = useCallback(
    (zoneNumber) => {
      if (selectedZones.includes(zoneNumber)) {
        setSelectedZones([]);
        setZoneRectangles((prevRectangles) => ({
          ...prevRectangles,
          [zoneNumber]: prevRectangles?.[zoneNumber]?.map((rect) => ({
            ...rect,
            visible: false,
          })),
        }));
        return;
      }

      setSelectedZones([zoneNumber]);
      setZoneRectangles((prevRectangles) => ({
        ...prevRectangles,
        [zoneNumber]: getAllZones(zoneNumber),
      }));
    },
    [getAllZones, selectedZones]
  );

  const countEventsInRectangles = useCallback(
    (rectangles, events) => {
      return rectangles.map((rect) => {
        const count = events?.filter((event) => {
          const pos = convertCoordinatesWithPadding(event.coord_x, event.coord_y);

          return (
            pos.x >= rect.x &&
            pos.x <= rect.x + rect.width &&
            pos.y >= rect.y &&
            pos.y <= rect.y + rect.height
          );
        }).length;

        return {
          ...rect,
          eventCount: count,
        };
      });
    },
    [convertCoordinatesWithPadding]
  );

  const handleRemoveRectangle = useCallback(
    (zoneNumber, index) => {
      const updatedRectangles = { ...zoneRectangles };
      const rectangle = updatedRectangles[zoneNumber][index];
      rectangle.visible = !rectangle.visible;

      if (!rectangle.visible) {
        const markersInRectangle = displayedEvents?.filter((event) => {
          const pos = convertCoordinatesWithPadding(event.coord_x, event.coord_y);

          return (
            pos.x >= rectangle.x &&
            pos.x <= rectangle.x + rectangle.width &&
            pos.y >= rectangle.y &&
            pos.y <= rectangle.y + rectangle.height
          );
        });

        setZoneMarkers((prev) => {
          const ids = new Set(prev || []);
          markersInRectangle?.forEach((marker) => ids.add(marker.id));
          return Array.from(ids);
        });
      }

      setZoneRectangles(updatedRectangles);
    },
    [convertCoordinatesWithPadding, displayedEvents, zoneRectangles]
  );

  const toggleDrawingMode = useCallback(() => {
    setDrawing((prev) => !prev);
    setPreviewShape(null);
    setDrawnShapes([]);
  }, []);

  const resetDrawingZones = useCallback(() => {
    setSelectedZones([]);
    setZoneRectangles({});
    setDrawnShapes([]);
    setPreviewShape(null);
    setDrawMarkers([]);
    setZoneMarkers([]);
    setDrawing(false);
    setDrawingStarted(false);
  }, []);

  const renderDrawnShapes = useCallback(() => {
    return drawnShapes.map((shape, index) => (
      <g key={index}>
        {shape.type === "rectangle" && (
          <rect
            id={shape.id}
            x={shape.x}
            y={shape.y}
            width={shape.width}
            height={shape.height}
            fill="#ffffff29"
            stroke="red"
            strokeWidth="2"
            cursor="move"
            className="custom-shape"
          />
        )}
      </g>
    ));
  }, [drawnShapes]);

  const renderPreviewShape = useCallback(() => {
    if (!(previewShape && previewShape.type === "rectangle")) {
      return null;
    }

    return (
      <rect
        x={previewShape.x}
        y={previewShape.y}
        width={previewShape.width}
        height={previewShape.height}
        fill="transparent"
        stroke="blue"
        strokeWidth="2"
        strokeDasharray="4"
      />
    );
  }, [previewShape]);

  const renderZones = useCallback(() => {
    return selectedZones.map((zoneNumber) =>
      countEventsInRectangles(
        zoneRectangles[zoneNumber] || [],
        displayedEvents || []
      ).map((rectangle, index) => (
        <g
          key={`zone-${zoneNumber}-${index}`}
          className={`zone-${rectangle?.id || index}`}
          onClick={() => handleRemoveRectangle(zoneNumber, index)}
        >
          <rect
            style={{ opacity: rectangle.visible ? 1 : 0.5 }}
            x={rectangle.x}
            y={rectangle.y}
            width={rectangle.width}
            height={rectangle.height}
            fill="rgba(0, 123, 255, 0.2)"
            stroke="#fff"
            strokeWidth={2}
          />
          <circle
            cx={rectangle.x + rectangle.width / 2}
            cy={rectangle.y + rectangle.height / 2}
            r="30"
            style={{
              strokeWidth: 1,
              stroke: "white",
              fill: "rgba(104, 134, 189, 0.68)",
            }}
          />
        </g>
      ))
    );
  }, [
    countEventsInRectangles,
    displayedEvents,
    handleRemoveRectangle,
    selectedZones,
    zoneRectangles,
  ]);

  const getTotalEventsInUnselectedRectangles = useCallback(() => {
    if (selectedZones.length === 0 || displayedEvents?.length === 0) {
      return 0;
    }

    const rectanglesInSelectedZones = selectedZones.flatMap(
      (zoneNumber) => zoneRectangles[zoneNumber] || []
    );

    const rectanglesWithCounts = countEventsInRectangles(
      rectanglesInSelectedZones,
      displayedEvents || []
    );

    return rectanglesWithCounts
      .filter((rect) => !rect.visible)
      .reduce((total, rect) => total + rect.eventCount, 0);
  }, [countEventsInRectangles, displayedEvents, selectedZones, zoneRectangles]);

  const eventsCount = useMemo(() => {
    if (selectedZones.length > 0) {
      return getTotalEventsInUnselectedRectangles();
    }

    return displayedEvents?.filter((e) => e.coord_x != null && e.coord_y != null).length || 0;
  }, [displayedEvents, getTotalEventsInUnselectedRectangles, selectedZones.length]);

  const isDotInsideRectangle = useCallback(
    (dot, rectangle) => {
      const pos = convertCoordinatesWithPadding(dot.coord_x, dot.coord_y);

      return (
        pos.x >= rectangle.x &&
        pos.x <= rectangle.x + rectangle.width &&
        pos.y >= rectangle.y &&
        pos.y <= rectangle.y + rectangle.height
      );
    },
    [convertCoordinatesWithPadding]
  );

  const isDotInsideAnyRectangle = useCallback(
    (dot, rectangles) => {
      return rectangles.some((rectangle) => isDotInsideRectangle(dot, rectangle));
    },
    [isDotInsideRectangle]
  );

  const isDotInsideDrawnShapes = useCallback(
    (dot) => {
      return isDotInsideAnyRectangle(dot, drawnShapes);
    },
    [drawnShapes, isDotInsideAnyRectangle]
  );

  return {
    pitchRef,
    drawing,
    toggleDrawingMode,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    selectedZones,
    setSelectedZones,
    zoneRectangles,
    setZoneRectangles,
    handleZoneSelection,
    drawMarkers,
    zoneMarkers,
    eventsCount,
    renderZones,
    renderDrawnShapes,
    renderPreviewShape,
    isDotInsideAnyRectangle,
    isDotInsideDrawnShapes,
    resetDrawingZones,
  };
};

export default usePitchDrawingZones;
